/**
 * Action point click handler
 * Handles what happens when an action point is clicked
 */

import { scrollManager } from '../../../utils/scroll'
import { getAudioManager } from '../../../features/audio'
import {
  createBubbleImage,
  animateBubblePopOut,
  setupBubbleClickAndHover,
  ensureBubbleVisible,
  getBubbleImageForActionPoint,
  getBubbleIdForActionPoint,
} from './bubble-manager'
import { debugLog } from '../../../features/debug'
import type { SceneData, SceneConfig, ChoiceCondition } from '../types'
import { filterBubblesByCondition } from './bubble-filter'
import { pausePreload, resumePreload } from '../../story-engine/preload'

export interface ActionPointClickContext {
  data: SceneData
  config: SceneConfig
  imageContainer: HTMLDivElement | null
  clickedActionPointIndices: Set<number>
  actionPoints: HTMLDivElement[]
  currentActionPointIndex: number
  unlockedBubbleIds: Set<string>
  onBubbleShown?: (bubbleId: string) => void
  onActionPointClicked?: (actionPointIndex: number) => void
  onUnlock?: () => void
  onShowChoices?: () => void
  onCreateActionPoint: (xPercent: number, yPercent: number, bubbleIndex: number, actionPointIndex: number) => void
  onEvaluateCondition?: (condition: ChoiceCondition) => boolean
}

/**
 * Handle action point click
 */
export function handleActionPointClick(
  actionPoint: HTMLDivElement,
  bubbleIndex: number,
  context: ActionPointClickContext,
  wasKeyboard?: boolean
): void {
  // Use filtered bubbles (bubbleIndex refers to filtered array)
  const speechBubbles = filterBubblesByCondition(
    context.data.speechBubbles,
    context.onEvaluateCondition
  )
  const bubble = speechBubbles[bubbleIndex]
  
  // Find original index in the full array for bubble image lookup
  const originalIndex = context.data.speechBubbles?.findIndex((b) => b.id === bubble.id) ?? bubbleIndex

  pausePreload()

  context.clickedActionPointIndices.add(bubbleIndex)
  playBubbleSoundAndLog(bubbleIndex, bubble, context.data.id)
  removeActionPointFromList(actionPoint, context.actionPoints)
  context.onActionPointClicked?.(bubbleIndex)

  const bubbleImageData = getBubbleImageForActionPoint(context.data, originalIndex)
  if (bubbleImageData && bubble && context.imageContainer) {
    showBubbleImage(context, bubbleIndex, bubble, bubbleImageData, wasKeyboard)
  } else {
    resumePreload()
  }

  handleScrollUnlock(context, speechBubbles)
}

function getBubbleSoundIndex(bubble: { id?: string } | undefined, bubbleIndex: number): number {
  if (bubble?.id) {
    const parsed = parseInt(bubble.id.split('-').pop() || String(bubbleIndex + 1), 10)
    return Number.isNaN(parsed) ? bubbleIndex + 1 : parsed
  }
  return bubbleIndex + 1
}

function playBubbleSoundAndLog(
  bubbleIndex: number,
  bubble: { id?: string } | undefined,
  sceneId: string
): void {
  const soundIndex = getBubbleSoundIndex(bubble, bubbleIndex)
  debugLog(
    `[StoryScene] Playing bubble sound: bubble-${soundIndex}.mp3 (bubbleIndex: ${bubbleIndex}, bubble.id: ${bubble?.id}, sceneId: ${sceneId})`
  )
  getAudioManager().playBubbleSound(soundIndex, sceneId)
}

function removeActionPointFromList(
  actionPoint: HTMLDivElement,
  actionPoints: HTMLDivElement[]
): void {
  actionPoint.remove()
  const index = actionPoints.indexOf(actionPoint)
  if (index > -1) actionPoints.splice(index, 1)
}

function showBubbleImage(
  context: ActionPointClickContext,
  bubbleIndex: number,
  bubble: { position: { x: number; y: number }; id?: string },
  bubbleImageData: { image: string; width?: number },
  wasKeyboard?: boolean
): void {
  const bubbleImg = createBubbleImage(
    bubbleImageData.image,
    bubble.position.x,
    bubble.position.y,
    bubbleImageData.width,
    bubbleIndex
  )
  context.imageContainer!.appendChild(bubbleImg)

  const innerImg = bubbleImg.querySelector('img') as HTMLImageElement | null
  if (innerImg && !innerImg.complete) {
    innerImg.addEventListener('load', () => resumePreload(), { once: true })
    innerImg.addEventListener('error', () => resumePreload(), { once: true })
  } else {
    resumePreload()
  }

  // Use bubble.id directly if available, otherwise derive from filtered index
  const bubbleId = bubble.id || `bubble-${bubbleIndex + 1}`
  context.unlockedBubbleIds.add(bubbleId)

  // For setupBubbleClickAndHover, we need the original index to get the correct bubble data
  const originalIndex = context.data.speechBubbles?.findIndex((b) => b.id === bubble.id) ?? bubbleIndex
  animateBubblePopOut(bubbleImg, bubbleIndex, context.config, (img, idx) => {
    setupBubbleClickAndHover(img, originalIndex, context.data)
  })
  ensureBubbleVisible(bubbleImg, { skipScroll: !wasKeyboard })
  context.onBubbleShown?.(bubbleId)
  
  // Only show choices after all action points have been clicked
  if (context.data.choices && context.onShowChoices) {
    // Use filtered bubbles for counting
    const filteredBubbles = filterBubblesByCondition(
      context.data.speechBubbles,
      context.onEvaluateCondition
    )
    const totalActionPoints = filteredBubbles.filter(
      (b) => b.actionPoint && b.actionPoint.position
    ).length
    const clickedActionPoints = context.clickedActionPointIndices.size
    
    // Show choices only when the last action point is clicked
    if (clickedActionPoints >= totalActionPoints) {
      context.onShowChoices()
    }
  }
}

function handleScrollUnlock(
  context: ActionPointClickContext,
  speechBubbles: Array<{ actionPoint?: { position?: { x: number; y: number } }; id?: string }>
): void {
  if (speechBubbles.length === 1) {
    scrollManager.removeMaxScroll()
    context.onUnlock?.()
    return
  }

  if (context.currentActionPointIndex < speechBubbles.length) {
    const nextBubble = speechBubbles[context.currentActionPointIndex]
    if (!nextBubble?.actionPoint?.position) {
      console.error(
        `Scene ${context.data.id}: speechBubble "${nextBubble?.id}" is missing required "actionPoint.position"`
      )
      return
    }
    const pos = nextBubble.actionPoint!.position
    context.onCreateActionPoint(
      pos.x,
      pos.y,
      context.currentActionPointIndex,
      context.currentActionPointIndex
    )
  } else {
    scrollManager.removeMaxScroll()
    context.onUnlock?.()
  }
}
